// coverage.ts — viewpoint coverage for the Capture screen's ring indicator.
// Each tracked pose's camera centre is binned by azimuth (around world +Z,
// about the reference-marker origin) and elevation above the marker plane,
// so the user can see which sides of the object still need a pass.

import { poseCameraCenter } from '../splat/math';
import type { Pose } from '../api';

export const AZIMUTH_BINS = 16;
export const ELEVATION_BANDS = [15, 40, 65, 90]; // upper edges, degrees

export class Coverage {
  readonly hits: number[][]; // [band][azimuth bin] → keyframe count

  constructor() {
    this.hits = ELEVATION_BANDS.map(() => new Array<number>(AZIMUTH_BINS).fill(0));
  }

  // add records one camera pose; returns the bin it fell in, or null when the
  // camera is below the marker plane.
  add(pose: Pose, center: [number, number, number] = [0, 0, 0]): { band: number; bin: number } | null {
    const bin = viewpointBin(pose, center);
    if (bin) this.hits[bin.band][bin.bin]++;
    return bin;
  }

  // fraction is the share of (band, azimuth) cells with ≥1 hit, over the
  // three bands a hand-held sweep can reach (the top band is near-vertical).
  fraction(): number {
    let filled = 0, total = 0;
    for (let b = 0; b < this.hits.length - 1; b++) {
      for (const n of this.hits[b]) {
        total++;
        if (n > 0) filled++;
      }
    }
    return total ? filled / total : 0;
  }
}

export function viewpointBin(pose: Pose, center: [number, number, number] = [0, 0, 0]): { band: number; bin: number } | null {
  const c = poseCameraCenter(pose.R, pose.T);
  const dx = c[0] - center[0], dy = c[1] - center[1], dz = c[2] - center[2];
  const horiz = Math.hypot(dx, dy);
  const elev = (Math.atan2(dz, horiz) * 180) / Math.PI;
  if (elev < 0) return null;
  let az = Math.atan2(dy, dx);
  if (az < 0) az += 2 * Math.PI;
  const bin = Math.min(AZIMUTH_BINS - 1, Math.floor((az / (2 * Math.PI)) * AZIMUTH_BINS));
  let band = ELEVATION_BANDS.findIndex((top) => elev <= top);
  if (band < 0) band = ELEVATION_BANDS.length - 1;
  return { band, bin };
}
